import { useQueryClient, type DefaultError } from '@tanstack/vue-query'
import { extractErrorMsgFromFailedRequest } from '~/utils/request-helpers'
import type { NitroFetchOptions, NitroFetchRequest } from 'nitropack'
import type { NotificationSubscribedVariantIds } from '~/types/api/bff/web/notification-subscription/remind-me.types'

interface remindMeParams {
  variantId: string
  eventName: string
  eventType: string
}

export const useCreateRemindMeVariantMutation = (
  userId: MaybeRefOrGetter<string | undefined>,
  options?: NitroFetchOptions<NitroFetchRequest>
) => {
  const {
    $notificationSubscriptionCreateRepository,
    $notificationSubscriptionListRepository,
  } = useNuxtApp()
  const client = useQueryClient()
  const { notify } = useNotification()
  const { t } = useI18n()

  return {
    mutationKey: getRemindMeMutationKey(userId),
    mutationFn: ({ variantId, eventName, eventType }: remindMeParams) => {
      const payload = {
        subscribable_type: 'Variant',
        subscribable_id: variantId,
        event_type: eventType,
        event_name: eventName,
      }
      return $notificationSubscriptionCreateRepository.create(
        payload,
        unref(options)
      )
    },
    onSuccess: async () => {
      const subscriptions: NotificationSubscribedVariantIds =
        await $notificationSubscriptionListRepository.index({
          query: { 'page[size]': 200, 'page[number]': 1 },
        })

      const resolvedUserId = unref(userId) as string | undefined

      updateLocalStorageNotificationSubscribedIds(resolvedUserId, subscriptions)

      client.setQueryData(getRemindMeQueryKey(userId), subscriptions)

      notify({
        type: 'success',
        text: t('earlyAccess.successfullyNotified'),
      })
    },
    onError(error: DefaultError) {
      if (error?.statusCode !== 422) {
        notify({
          type: 'error',
          text: extractErrorMsgFromFailedRequest(error),
        })
      }
    },
  }
}
